import { useState } from "react";
import {
  format,
  getISOWeek,
  getYear,
  getQuarter,
  subDays, subWeeks, subMonths, subQuarters, subYears,
  addDays, addWeeks, addMonths, addQuarters, addYears,
  startOfDay, startOfWeek, startOfMonth, startOfQuarter, startOfYear,
  startOfISOWeek, endOfISOWeek,
} from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { DayPicker } from "react-day-picker";
import "react-day-picker/style.css";
import { CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../../client/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../../client/components/ui/popover";
import { cn } from "../../client/utils";
import { useLanguageContext, type Language } from "../../client/contexts/LanguageContext";
import useTranslation from "../../client/hooks/useTranslation";
import type { ReportPeriod } from "../metrics/types";

interface Props {
  period: ReportPeriod;
  referenceDate: Date;
  onChange: (date: Date) => void;
  disableNext: boolean;
  onPrev: () => void;
  onNext: () => void;
}

export function PeriodNavBar({ period, referenceDate, onChange, disableNext, onPrev, onNext }: Props) {
  const { language } = useLanguageContext();
  const t = useTranslation();
  const locale = language === "fr" ? fr : enUS;
  const [open, setOpen] = useState(false);

  const label = formatPeriodLabel(period, referenceDate, locale, language);
  const today = new Date();

  // Build a list of recent shortcuts depending on the period type
  const shortcuts = buildShortcuts(period, today, language, locale);

  function handleShortcut(date: Date) {
    onChange(date);
    setOpen(false);
  }

  function handleDaySelect(day: Date | undefined) {
    if (!day) return;
    onChange(day);
    setOpen(false);
  }

  // For weekly period, highlight the full week of the selected day in the calendar
  const weekSelected =
    period === "weekly"
      ? { from: startOfISOWeek(referenceDate), to: endOfISOWeek(referenceDate) }
      : undefined;

  return (
    <div className="flex items-center gap-2 mt-2">
      <Button
        type="button"
        variant="outline"
        size="icon"
        aria-label={t.reports.periodNavPrev}
        onClick={onPrev}
        className="h-8 w-8 shrink-0"
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="flex-1 h-8 gap-1.5 text-sm font-medium tabular-nums justify-center"
          >
            <CalendarIcon className="h-3.5 w-3.5 opacity-60 shrink-0" />
            {label}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="center">
          <div className="flex">
            {/* Shortcuts list */}
            <div className="flex flex-col border-r py-2 min-w-[160px]">
              {shortcuts.map((s) => (
                <button
                  key={s.label}
                  type="button"
                  onClick={() => handleShortcut(s.date)}
                  className={cn(
                    "px-4 py-1.5 text-sm text-left hover:bg-muted transition-colors",
                    formatPeriodLabel(period, s.date, locale, language) === label &&
                      "font-semibold text-[#00C9A7]"
                  )}
                >
                  {s.label}
                </button>
              ))}
            </div>

            {/* Calendar — drp-calendar applies Edgely teal theme from Main.css */}
            <div className="drp-calendar">
              {period === "weekly" ? (
                <DayPicker
                  mode="range"
                  selected={weekSelected}
                  onSelect={(range) => handleDaySelect(range?.from)}
                  defaultMonth={referenceDate}
                  disabled={{ after: today }}
                  locale={locale}
                  weekStartsOn={1}
                  className="p-3"
                />
              ) : (
                <DayPicker
                  mode="single"
                  selected={referenceDate}
                  onSelect={handleDaySelect as any}
                  defaultMonth={referenceDate}
                  disabled={{ after: today }}
                  locale={locale}
                  weekStartsOn={1}
                  className="p-3"
                />
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <Button
        type="button"
        variant="outline"
        size="icon"
        aria-label={t.reports.periodNavNext}
        onClick={onNext}
        disabled={disableNext}
        className="h-8 w-8 shrink-0"
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
}

interface Shortcut {
  label: string;
  date: Date;
}

function buildShortcuts(
  period: ReportPeriod,
  today: Date,
  language: Language,
  locale: typeof fr,
): Shortcut[] {
  const isFr = language === "fr";
  const shortcuts: Shortcut[] = [];

  switch (period) {
    case "daily":
      for (let i = 0; i < 7; i++) {
        const d = subDays(today, i);
        shortcuts.push({
          label: i === 0
            ? (isFr ? "Aujourd'hui" : "Today")
            : i === 1
              ? (isFr ? "Hier" : "Yesterday")
              : format(d, isFr ? "EEEE d MMM" : "EEE, MMM d", { locale }),
          date: d,
        });
      }
      break;
    case "weekly":
      for (let i = 0; i < 8; i++) {
        const d = subWeeks(today, i);
        const wStart = startOfISOWeek(d);
        const wEnd = endOfISOWeek(d);
        const sameMonth = wStart.getMonth() === wEnd.getMonth();
        const weekRange = sameMonth
          ? `${format(wStart, "d", { locale })}–${format(wEnd, "d MMM", { locale })}`
          : `${format(wStart, "d MMM", { locale })}–${format(wEnd, "d MMM", { locale })}`;
        shortcuts.push({
          label: i === 0
            ? (isFr ? `Cette semaine · ${weekRange}` : `This week · ${weekRange}`)
            : i === 1
              ? (isFr ? `Sem. dernière · ${weekRange}` : `Last week · ${weekRange}`)
              : weekRange,
          date: d,
        });
      }
      break;
    case "monthly":
      for (let i = 0; i < 12; i++) {
        const d = subMonths(today, i);
        shortcuts.push({
          label: i === 0
            ? (isFr ? "Ce mois" : "This month")
            : i === 1
              ? (isFr ? "Mois dernier" : "Last month")
              : format(d, "MMMM yyyy", { locale }),
          date: d,
        });
      }
      break;
    case "quarterly":
      for (let i = 0; i < 8; i++) {
        const d = subQuarters(today, i);
        const q = getQuarter(d);
        const year = getYear(d);
        shortcuts.push({
          label: i === 0
            ? (isFr ? "Ce trimestre" : "This quarter")
            : i === 1
              ? (isFr ? "Trim. dernier" : "Last quarter")
              : (isFr ? `T${q} ${year}` : `Q${q} ${year}`),
          date: d,
        });
      }
      break;
    case "annual":
      for (let i = 0; i < 5; i++) {
        const d = subYears(today, i);
        const year = getYear(d);
        shortcuts.push({
          label: i === 0
            ? (isFr ? "Cette année" : "This year")
            : i === 1
              ? (isFr ? "Année dernière" : "Last year")
              : String(year),
          date: d,
        });
      }
      break;
  }

  return shortcuts;
}

function formatPeriodLabel(
  period: ReportPeriod,
  date: Date,
  locale: typeof fr,
  language: Language,
): string {
  switch (period) {
    case "daily":
      return format(date, language === "fr" ? "EEEE d MMMM yyyy" : "EEEE, MMMM d, yyyy", { locale });
    case "weekly": {
      const wStart = startOfISOWeek(date);
      const wEnd = endOfISOWeek(date);
      const sameMonth = wStart.getMonth() === wEnd.getMonth();
      const sameYear = wStart.getFullYear() === wEnd.getFullYear();
      if (sameMonth) {
        return `${format(wStart, "d", { locale })}–${format(wEnd, "d MMM yyyy", { locale })}`;
      }
      if (sameYear) {
        return `${format(wStart, "d MMM", { locale })} – ${format(wEnd, "d MMM yyyy", { locale })}`;
      }
      return `${format(wStart, "d MMM yyyy", { locale })} – ${format(wEnd, "d MMM yyyy", { locale })}`;
    }
    case "monthly":
      return format(date, "MMMM yyyy", { locale });
    case "quarterly": {
      const q = getQuarter(date);
      const year = getYear(date);
      return language === "fr" ? `T${q} ${year}` : `Q${q} ${year}`;
    }
    case "annual":
      return String(getYear(date));
    default: {
      const _exhaustive: never = period;
      return String(_exhaustive);
    }
  }
}
